import { sum } from 'lodash';
import { CanvaSX, normalizeNode, renderNode } from '../canvasx';
import { defaultShapeProps, normalizeGap, normalizeSpacing } from '../shapes';
import {
  calculateDimensionsForNode,
  drawRect,
  isElement,
  drawSeparator,
  isElementOfType,
  calculateDimensionsForElement,
  getFlexGrowValue,
} from '../util';
import { Text, splitTextElement } from './Text';

export interface FlexChild {
  node: CanvaSXNode;
  width: number;
  height: number;
}

interface Row {
  children: FlexChild[];
  height: number;
}

export type RenderedDimensions = DimensionStruct & {
  rows: number;
  unwrappedWidth?: number;
};

const defaultFlexRowProps = {
  ...defaultShapeProps,
};

export type VerticalAlignment = 'top' | 'bottom' | 'center' | 'stretch';

export type FlexRowProps = Partial<ShapeStruct> &
  FlexContainer & {
    alignBottom?: boolean;
    verticalAlignment?: VerticalAlignment;
    gap?: SpacingAxisStruct | number;
    minHeight?: number;
    maxHeight?: number;
    maxRows?: number;
    setRenderedDimensions?: (dimensions: RenderedDimensions) => void;
    wrap?: boolean;

    // Internal, used to memoize layout
    _layout?: FlexRowLayout;
  };

interface FlexRowLayout extends DimensionStruct {
  rows: Row[];
  unwrappedWidth: number;
}

function alignRowItems(row: Row, verticalAlignment: VerticalAlignment) {
  const { height } = row;
  row.children.forEach(child => {
    const { node } = child;

    // Skip non-elements
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      return;
    }

    const diff = height - child.height;

    switch (verticalAlignment) {
      case 'bottom':
        node.props.y = diff;
        break;

      case 'center':
        node.props.y = diff / 2;
        break;

      case 'stretch':
        node.props.height = height;
        break;

      case 'top':
      default:
        // Do nothing, this is the default behaviour
        break;
    }
  });
}

function applyFlexGrow(row: Row, width: number, gap: number, separator = 0) {
  const availableWidth =
    width -
    sum(row.children.map(child => child.width)) -
    (row.children.length - 1) * gap -
    (row.children.length - 1) * separator;

  if (isNaN(availableWidth) || availableWidth <= 0) {
    return;
  }

  const flexGrowChildren = row.children.filter(
    child => isElement(child.node) && child.node.props.flexGrow
  );
  if (flexGrowChildren.length < 1) {
    return;
  }

  // Calculate total flex grow value (boolean true = 1)
  const totalFlexGrow = sum(
    flexGrowChildren.map(child => {
      if (isElement(child.node)) {
        return getFlexGrowValue(child.node.props.flexGrow);
      }
      return 0;
    })
  );

  flexGrowChildren.forEach(child => {
    if (isElement(child.node)) {
      const flexGrowValue = getFlexGrowValue(child.node.props.flexGrow);
      const extraWidth = (availableWidth * flexGrowValue) / totalFlexGrow;

      child.width += extraWidth;
      child.node.props.width ||= 0; // child may not have explicit width
      child.node.props.width += extraWidth;

      // Recalculate the dimensinos for the node
      calculateDimensionsForElement(child.node, true);
    }
  });
}

function calculateUnwrappedWidth(
  padding: SpacingSidesStruct,
  childrenWidths: FlexChild[],
  gapSpacing: SpacingAxisStruct,
  separatorSize = 0
): number {
  let unwrappedWidth = padding.left + padding.right;
  childrenWidths.forEach((rowChild, index) => {
    unwrappedWidth += rowChild.width;
    if (index > 0) {
      unwrappedWidth += gapSpacing.horizontal + separatorSize;
    }
  });
  return unwrappedWidth;
}

// If the width of the FlexRow is not defined, this will return the calculated width
function generateLayout(props: ElementProps<FlexRowProps>): FlexRowLayout {
  if (props._layout) {
    return props._layout;
  }

  const { ctx } = CanvaSX.context;
  let {
    verticalAlignment = 'top',
    children = [],
    gap,
    minHeight,
    maxHeight,
    maxRows,
    separator,
    width,
    wrap = true,
  } = props;

  children = Array.isArray(children) ? children : [children];

  // Remove empty children
  children = children.filter(Boolean);

  const gapSpacing = normalizeGap(gap);
  const padding = normalizeSpacing(props.padding);
  const separatorSize = separator?.lineWidth ?? 0;

  // Calculate the widths of each child item
  const childrenWidths: FlexChild[] =
    children.map(child => {
      const dimensions = calculateDimensionsForNode(child);

      return {
        node: child,
        ...dimensions,
      };
    }) || [];

  const unwrappedWidth = calculateUnwrappedWidth(
    padding,
    childrenWidths,
    gapSpacing,
    separatorSize
  );

  // Generate/wrap rows based on child widths
  let rows: Row[] = [];
  if (typeof width === 'number' && wrap) {
    const actualWidth = Math.max(width - padding.left - padding.right, 1);

    let currentRow: Row = { children: [], height: 0 };
    rows.push(currentRow);
    let currentWidth = 0;
    for (let index = 0; index < childrenWidths.length; index++) {
      const rowChild = childrenWidths[index];
      const remainingWidth =
        actualWidth - currentWidth - gapSpacing.horizontal - separatorSize;

      if (
        rowChild.width > remainingWidth &&
        (currentWidth > 0 || isElementOfType(rowChild.node, Text))
      ) {
        // If it's a text node, see if we can wrap the text
        if (isElementOfType(rowChild.node, Text)) {
          const wrappedElements = splitTextElement(
            ctx,
            rowChild.node,
            remainingWidth
          );

          if (wrappedElements) {
            const [firstElement, wrappedElement] = wrappedElements;

            Object.assign(rowChild, {
              node: firstElement,
              ...firstElement.props._dimensions,
            });
            const wrappedChild: FlexChild = {
              node: wrappedElement,
              ...wrappedElement.props._dimensions!,
            };

            childrenWidths.splice(index + 1, 0, wrappedChild);

            // Add the first node to the current row before wrapping, and swap out the wrapped node to the next row
            currentRow.children.push(rowChild);
          } else if (currentRow.children.length === 0) {
            // The current row is empty, so we need to prevent an infinite loop
            // This might happen if the text of a child is so close to the available width that we have a rounding error…
            currentRow.children.push(rowChild);
            continue;
          } else {
            // We couldn't fit any text on this row, but the text might still be too long to fit on one row
            // Create a new empty row and try wrapping the same text element
            index--;
          }

          // Create a new empty row
          currentRow = { children: [], height: 0 };
          rows.push(currentRow);
          currentWidth = 0;
          continue;
        }

        // Can't fit on row, break to new row
        currentRow = { children: [rowChild], height: 0 };
        rows.push(currentRow);
        currentWidth = rowChild.width;
      } else {
        if (currentRow.children.length > 0) {
          currentWidth += gapSpacing.horizontal + separatorSize;
        }
        currentWidth += rowChild.width;
        currentRow.children.push(rowChild);
      }
    }

    // Remove empty row
    if (currentRow.children.length === 0) {
      rows.pop();
    }

    // Apply flex grow
    rows.forEach(row =>
      applyFlexGrow(row, actualWidth, gapSpacing.horizontal, separatorSize)
    );
  } else {
    width = unwrappedWidth;

    if (children.length) {
      const row: Row = { children: [], height: 0 };
      childrenWidths.forEach(rowChild => {
        row.children.push(rowChild);
      });

      rows.push(row);
    }
  }

  // Calculate height for each row
  rows.forEach(row => {
    row.height = Math.max(...row.children.map(child => child.height));
  });

  // Apply max rows
  if (maxRows) {
    rows = rows.slice(0, maxRows);
  } else if (maxHeight) {
    let currentHeight = 0;
    const newRows = rows.slice(0, 1);

    // Add rows until we pass the height
    for (let y = 1; y < rows.length; y++) {
      const row = rows[y];
      const nextHeight = currentHeight + gapSpacing.vertical + row.height;
      if (nextHeight > maxHeight) {
        break;
      } else {
        newRows.push(row);
        currentHeight = nextHeight;
      }
    }

    rows = newRows;
  }

  // Ensure we meet minHeight, otherwise add remaining height to the last row
  if (minHeight && rows.length > 0) {
    const totalHeight = sum(rows.map(row => row.height));

    if (totalHeight < minHeight) {
      rows[rows.length - 1].height += minHeight - totalHeight;
    }
  }

  rows.forEach(row => alignRowItems(row, verticalAlignment));

  const height =
    sum(rows.map(row => row.height)) +
    (rows.length - 1) * gapSpacing.vertical +
    padding.top +
    padding.bottom;

  props._layout = { rows, width, height, unwrappedWidth };

  return props._layout;
}

/**
 * The FlexRow component will render child components in a flexbox-like fashion.
 * It will calculate the widths and heights of the children, and then wrap them
 * into rows as space allows. a `maxHeight` can be specified, and it will hide
 * any children rows past that height.
 *
 * A `setRenderedDimensions` callback can be used to be notified of the final
 * dimensions of the rendered rows.
 */
export const FlexRow: CanvaSXComponent<
  FlexRowProps,
  typeof defaultFlexRowProps
> & {
  calculateDimensions: (props: ElementProps<FlexRowProps>) => DimensionStruct;
  recalculateDimensions: (props: ElementProps<FlexRowProps>) => DimensionStruct;
} = function (props) {
  const { ctx } = CanvaSX.context;
  let { alignBottom, gap, setRenderedDimensions, width, separator, border } =
    props;

  const gapSpacing = normalizeGap(gap);

  const layout = generateLayout(props);
  const { rows, unwrappedWidth } = layout;
  width = layout.width;
  const contentHeight = layout.height;

  let { y = 0, height = contentHeight } = props;
  if (alignBottom) {
    if (typeof props.height !== 'number') {
      throw new Error('Cannot use alignBottom without providing a height.');
    }

    const bottom = y + props.height;
    y = bottom - contentHeight;
  }

  height = contentHeight;

  // Call setRenderedDimensions
  if (setRenderedDimensions) {
    setRenderedDimensions({ width, height, rows: rows.length, unwrappedWidth });
  }

  // Set mountedObject dimensions
  props._dimensions = { width, height };

  drawRect(ctx, { ...props, y, width, height }, () => {
    rows.forEach(row => {
      // Save initial coordinates of row
      ctx.save();

      row.children.forEach(({ node, width }, i) => {
        if (separator && i > 0) {
          drawSeparator({ ctx, width, height, separator, border });
        }

        renderNode(node);

        ctx.translate(width + gapSpacing.horizontal, 0);
      });

      // Restore to original coordinates of row, then add a row/gap to move down one row (but same X)
      ctx.restore();
      ctx.translate(0, row.height + gapSpacing.vertical);
    });
  });

  return null;
};

FlexRow.defaultProps = defaultFlexRowProps;

FlexRow.calculateDimensions = (props: ElementProps<FlexRowProps>) => {
  // Normalize children into measurable nodes/elements
  props.children = normalizeNode(props.children as CanvaSXNode);

  const layout = generateLayout(props);

  const { width, height } = layout;
  Object.assign(props, { width, height });

  return { width, height };
};

FlexRow.recalculateDimensions = (props: ElementProps<FlexRowProps>) => {
  // Reset the layout, then recalculate
  delete props._layout;

  return FlexRow.calculateDimensions(props);
};
